import produce, { castDraft } from 'immer';
import { useRecoilCallback } from 'recoil';

import { FileIdsValue } from '../../../types/DynamicSchema';
import { FileReference, UploadingFile } from '../../../types/Files';
import {
  editingProjectAssetFilesStateAtom,
  editingProjectStateAtom,
  editingProjectStateErrorsAtom,
} from '../atoms/editingProjectStateAtom';
import { validateState } from '../domain/stateValidator';

export const useAssetFiles = () => {
  const removeAssetFile = useRecoilCallback(
    ({ snapshot, set }) => async (fileReference: FileReference, assetCategoryFieldKey: string) => {
      const state = await snapshot.getPromise(editingProjectStateAtom);
      const assetFilesState = await snapshot.getPromise(editingProjectAssetFilesStateAtom);

      if (state.assetCategoryValue == null) return;

      const updatedAssetCategoryValue = produce(state.assetCategoryValue.values, (draft) => {
        draft[assetCategoryFieldKey] = (draft[assetCategoryFieldKey] as FileIdsValue).filter(
          (fileId) => fileId !== fileReference.id,
        );
      });

      const updatedState = produce(state, (draft) => {
        if (draft.assetCategoryValue == null) return;
        draft.assetCategoryValue.values = updatedAssetCategoryValue;
      });

      set(
        editingProjectAssetFilesStateAtom,
        produce(assetFilesState, (draft) => {
          draft.assetFiles = assetFilesState.assetFiles.filter((file) => file.id !== fileReference.id);
        }),
      );
      set(editingProjectStateErrorsAtom, validateState(updatedState));
      set(editingProjectStateAtom, updatedState);
    },
  );

  const addAssetFile = useRecoilCallback(
    ({ snapshot, set }) => async (fileReference: FileReference, assetCategoryFieldKey: string) => {
      const state = await snapshot.getPromise(editingProjectStateAtom);
      const assetFilesState = await snapshot.getPromise(editingProjectAssetFilesStateAtom);

      if (state.assetCategoryValue == null) return;

      const updatedAssetCategoryValue = produce(state.assetCategoryValue.values, (draft) => {
        const assetFileIds = (draft[assetCategoryFieldKey] as Array<string>) || [];
        draft[assetCategoryFieldKey] = [...assetFileIds, fileReference.id];
      });

      const updatedState = produce(state, (draft) => {
        if (draft.assetCategoryValue == null) return;
        draft.assetCategoryValue.values = updatedAssetCategoryValue;
      });

      set(
        editingProjectAssetFilesStateAtom,
        produce(assetFilesState, (draft) => {
          draft.assetFiles.push(fileReference);
        }),
      );
      set(editingProjectStateErrorsAtom, validateState(updatedState));
      set(editingProjectStateAtom, updatedState);
    },
  );

  const addUploadingAssetFiles = useRecoilCallback(
    ({ snapshot, set }) => async (files: ReadonlyArray<UploadingFile>, fieldKey: string) => {
      const assetFilesState = await snapshot.getPromise(editingProjectAssetFilesStateAtom);

      set(
        editingProjectAssetFilesStateAtom,
        produce(assetFilesState, (draft) => {
          if (draft.uploadingAssetFiles[fieldKey] != null) {
            draft.uploadingAssetFiles[fieldKey] = draft.uploadingAssetFiles[fieldKey].concat(files);
          } else {
            draft.uploadingAssetFiles[fieldKey] = castDraft(files);
          }
        }),
      );
    },
  );

  const removeUploadingAssetFile = useRecoilCallback(
    ({ snapshot, set }) => async (file: File, assetCategoryFileKey: string) => {
      const assetFilesState = await snapshot.getPromise(editingProjectAssetFilesStateAtom);

      set(
        editingProjectAssetFilesStateAtom,
        produce(assetFilesState, (draft) => {
          draft.uploadingAssetFiles[assetCategoryFileKey] = assetFilesState.uploadingAssetFiles[
            assetCategoryFileKey
          ].filter((f) => f !== file);
        }),
      );
    },
  );

  const updateUploadingAssetFilesProgress = useRecoilCallback(
    ({ snapshot, set }) => async (
      file: UploadingFile | { id: string; name: string },
      fieldKey: string,
      progress: number,
      progressState: 'uploading' | 'downloading' = 'uploading',
    ) => {
      const assetFilesState = await snapshot.getPromise(editingProjectAssetFilesStateAtom);

      set(
        editingProjectAssetFilesStateAtom,
        produce(assetFilesState, (draft) => {
          if (draft.uploadingAssetFilesProgress[fieldKey] == null) {
            draft.uploadingAssetFilesProgress[fieldKey] = {
              [file.id]: { file, progress: 0, state: progressState },
            };
          }
          draft.uploadingAssetFilesProgress[fieldKey][file.id] = { file, progress, state: progressState };
        }),
      );
    },
  );

  const removeUploadingAssetFilesProgress = useRecoilCallback(
    ({ snapshot, set }) => async (file: UploadingFile, fieldKey: string) => {
      const assetFilesState = await snapshot.getPromise(editingProjectAssetFilesStateAtom);

      set(
        editingProjectAssetFilesStateAtom,
        produce(assetFilesState, (draft) => {
          delete draft.uploadingAssetFilesProgress[fieldKey][file.id];
        }),
      );
    },
  );

  return {
    removeAssetFile,
    addAssetFile,
    addUploadingAssetFiles,
    removeUploadingAssetFile,
    updateUploadingAssetFilesProgress,
    removeUploadingAssetFilesProgress,
  };
};
